/** 轻量 fetch 封装 — 自动带 Bearer token;401 时先用 refresh_token 单flight续期重放,
 * 续期失败才登出。登录请求自身的 401 不走进期逻辑,直接透传后端 detail(如"账号或密码错误").
 */

import { useAuthStore } from "./auth.ts";
import { apiErrorMessage } from "./apiError.ts";

const BASE = "/api/v1";

interface JsonOpts {
  signal?: AbortSignal;
  redirect?: RequestRedirect;
  method?: string;
  body?: unknown;
  headers?: Record<string, string>;
  query?: Record<string, string | number | boolean | undefined | string[]>;
}

interface TokenPair {
  access_token: string;
  refresh_token: string;
}

/** 不走进期续期的端点:登录本身的 401 是凭证错误,不是过期。 */
const NO_RETRY_PATHS = new Set(["/auth/login", "/auth/refresh"]);

function buildUrl(path: string, query?: JsonOpts["query"]): string {
  const url = BASE + path;
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null || v === "") continue;
    if (Array.isArray(v)) {
      for (const item of v) params.append(k, String(item));
    } else {
      params.set(k, String(v));
    }
  }
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

function doFetch(path: string, opts: JsonOpts): Promise<Response> {
  const headers: Record<string, string> = {
    Accept: "application/json",
    ...(opts.headers ?? {}),
  };
  if (opts.body !== undefined) {
    headers["Content-Type"] = "application/json";
  }
  const token = useAuthStore.getState().accessToken;
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return fetch(buildUrl(path, opts.query), {
    signal: opts.signal,
    redirect: opts.redirect,
    method: opts.method ?? "GET",
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
  });
}

// ===== token 续期(单flight)=====
// 并发请求同时拿到 401 时共享同一个刷新 Promise:只有一个真正打 /auth/refresh,
// 其余等待其结果,避免 refresh token 轮换(rotate)下第二次刷新用旧 token 被判吊销。
let refreshFlight: Promise<boolean> | null = null;

async function doRefresh(): Promise<boolean> {
  const refreshToken = useAuthStore.getState().refreshToken;
  if (!refreshToken) return false;
  try {
    const res = await fetch(buildUrl("/auth/refresh"), {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
    if (!res.ok) return false;
    const pair = (await res.json()) as TokenPair;
    return useAuthStore
      .getState()
      .refreshTokens(refreshToken, pair.access_token, pair.refresh_token);
  } catch {
    return false;
  }
}

function refreshSingleFlight(): Promise<boolean> {
  if (!refreshFlight) {
    refreshFlight = doRefresh().finally(() => {
      refreshFlight = null;
    });
  }
  return refreshFlight;
}

async function throwDetail(res: Response): Promise<never> {
  const text = await res.text().catch(() => "");
  throw new Error(apiErrorMessage(text, res.status));
}

export async function http<T = unknown>(path: string, opts: JsonOpts = {}): Promise<T> {
  const epoch = useAuthStore.getState().sessionEpoch;
  const checkOwner = () => {
    if (useAuthStore.getState().sessionEpoch !== epoch) throw new Error("登录状态已变更，请重试");
  };
  let res = await doFetch(path, opts);
  checkOwner();
  if (res.status === 401 && !NO_RETRY_PATHS.has(path)) {
    // access token 过期:先尝试续期,成功则用新 token 重放原请求
    if (await refreshSingleFlight()) {
      checkOwner();
      res = await doFetch(path, opts);
    }
  }
  checkOwner();
  if (res.status === 401) {
    if (NO_RETRY_PATHS.has(path)) {
      // 登录/刷新自身的 401:透传后端 detail(登录页据此展示"账号或密码错误")
      await throwDetail(res);
    }
    useAuthStore.getState().logout();
    throw new Error("登录已过期,请重新登录");
  }
  if (!res.ok) {
    await throwDetail(res);
  }
  if (res.status === 204) return undefined as T;
  const result = (await res.json()) as T;
  checkOwner();
  return result;
}

export const apiBase = BASE;

/** SSE 的 Authorization 也要手动注入(fetch 流). */
export function authHeaders(): Record<string, string> {
  const token = useAuthStore.getState().accessToken;
  return token ? { Authorization: `Bearer ${token}` } : {};
}
